import { useEffect, useState } from 'react';
import 'regenerator-runtime/runtime';
import { appContext } from '../AppContext';
import { GoFormzServices } from '../AppSettings';
import { apimCall, publicShareApimCall } from './api';
import { BulkActionItem } from '../components/ListPageActions/BulkActions';
import { DataSourceOrigin } from './constants';

export const getDataSourceRow = async (dataSourceName, key) => {
  const state = appContext.store.getState();
  const sharedAccessToken = state.formeditor.sharedAccessToken;
  try {
    const response = sharedAccessToken
      ? await publicShareApimCall(`/datasources/rowvalues?dataSourceName=${encodeURIComponent(dataSourceName)}&key=${encodeURIComponent(key)}`, 'GET')
      : await apimCall(GoFormzServices.DataSourceService, `/rows/values?dataSourceName=${encodeURIComponent(dataSourceName)}&key=${encodeURIComponent(key)}`);
    return response.data;
  } catch (err) {
    return null;
  }
};

export const getExternalDataSourceRow = async (dataSourceName, key, column) => {
  try {
    const response = await apimCall(
      GoFormzServices.DataSourceService,
      `/rows/external?key=${encodeURIComponent(key)}&dataSourceName=${encodeURIComponent(dataSourceName)}&columnName=${encodeURIComponent(column)}`
    );
    return response.data;
  } catch (err) {
    return null;
  }
};

export const getDataSource = async (dataSourceName) => {
  const state = appContext.store.getState();
  const sharedAccessToken = state.formeditor.sharedAccessToken;
  const response = sharedAccessToken
    ? await publicShareApimCall(`/datasources/select?name=${encodeURIComponent(dataSourceName)}`, 'GET')
    : await apimCall(GoFormzServices.DataSourceService, `/datasources/select?name=${encodeURIComponent(dataSourceName)}`);
  if (response.status === 200) {
    return response.data;
  }
  return null;
};

export function useDataSource(dataSourceName, dataSourceOrigin: DataSourceOrigin) {
  const [dataSource, setDataSource] = useState<TypeMePlease>();
  const [dataSourceLoaded, setDataSourceLoaded] = useState(false);

  useEffect(() => {
    if (dataSourceName && !dataSourceOrigin) {
      getDataSource(dataSourceName).then((d) => {
        setDataSource(d);
        setDataSourceLoaded(true);
      });
    }
  }, [dataSourceName]);

  return [dataSource, dataSourceLoaded];
}

export function getDataSourceIndexes(dataSource) {
  const indexMap = JSON.parse(dataSource.columnNames)._indexes;
  const indexes = [{ key: 'Key', value: 'KeyColumn' }];
  for (const property in indexMap) {
    // eslint-disable-next-line no-prototype-builtins
    if (indexMap.hasOwnProperty(property)) {
      indexes.push({ key: property, value: indexMap[property] });
    }
  }
  return indexes;
}

export async function getRemoteFilter(dataSource, filter, calcRunner, formValues, currentTableName) {
  let remoteFilter = '';
  if (calcRunner) {
    const indexes = getDataSourceIndexes(dataSource);
    remoteFilter = await calcRunner.getRemoteFilter(filter, indexes, formValues, currentTableName);
  }
  return remoteFilter;
}

export async function getExternalRemoteFilter(filter, calcRunner, formValues, currentTableName, dataSourceOrigin: DataSourceOrigin) {
  if (calcRunner) {
    return await calcRunner.getRemoteFilter(filter, null, formValues, currentTableName, dataSourceOrigin);
  }
}

const cache = {};

export const getDataSourceValues = async (dataSource, displayColumn, searchTerm, filter, calcRunner, formValues, currentTableName, searchOptions) => {
  const state = appContext.store.getState();
  const sharedAccessToken = state.formeditor.sharedAccessToken;
  const remoteFilter = await getRemoteFilter(dataSource, filter, calcRunner, formValues, currentTableName);

  const url = `/datasources/${dataSource.id}/indexsearch`;
  const body = {
    indexColumn: displayColumn,
    searchValue: searchTerm,
    filter: remoteFilter,
    searchOptions: searchOptions
  };
  const cacheKey = `url=${url}|body=${JSON.stringify(body)}`;
  if (cache[cacheKey]) {
    return cache[cacheKey];
  }

  const response = sharedAccessToken ? await publicShareApimCall(url, 'POST', body) : await apimCall(GoFormzServices.DataSourceService, url, 'POST', body);
  if (response.status === 200) {
    cache[cacheKey] = response.data;
    return response.data;
  }
  return null;
};

export function buildBulkActions(source: string): BulkActionItem[] {
  if (source === 'Datasource Editor') {
    return [{ name: 'Delete', type: 'delete' }];
  } else {
    return [
      { name: 'Delete', type: 'delete' },
      { name: 'Move', type: 'move' }
    ];
  }
}

export const getExternalDataSourceValues = async (
  dataSourceName: string,
  columnName: string,
  search: string,
  dataSourceOrigin: DataSourceOrigin,
  filter: string,
  calcRunner,
  formValues,
  currentTableName
) => {
  const remoteFilter = await getExternalRemoteFilter(filter, calcRunner, formValues, currentTableName, dataSourceOrigin);
  const response = await apimCall(
    GoFormzServices.DataSourceService,
    `datasources/external/columns?columnName=${encodeURIComponent(columnName)}&dataSourceName=${encodeURIComponent(dataSourceName)}&search=${encodeURIComponent(
      search
    )}&filter=${encodeURIComponent(remoteFilter ?? '')}`
  );

  if (response.status === 200) {
    return response.data;
  }
  return null;
};
